import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Save, Upload, Building2, Receipt, Banknote, Settings } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const tok = () => localStorage.getItem('admin_token') || '';
const auth = () => ({ headers: { Authorization: `Bearer ${tok()}` } });

const FIELDS = {
  company: {
    title: 'Company Info', icon: Building2,
    rows: [
      [['legal_name', 'Legal Name *'], ['trade_name', 'Trade Name']],
      [['address_line1', 'Address Line 1'], ['address_line2', 'Address Line 2']],
      [['city', 'City'], ['state', 'State'], ['state_code', 'State Code'], ['pincode', 'Pincode']],
      [['phone', 'Phone'], ['email', 'Email'], ['website', 'Website']],
      [['logo_url', 'Logo URL (paste image link)']],
    ],
  },
  tax: {
    title: 'GST / Tax Info', icon: Receipt,
    rows: [
      [['gstin', 'GSTIN (15-digit) *'], ['pan', 'PAN (10-char)'], ['cin', 'CIN']],
      [['default_gst_rate', 'Default GST Rate (%)'], ['hsn_purifiers', 'HSN — Purifiers'], ['hsn_spare_parts', 'HSN — Spare Parts'], ['hsn_services', 'HSN — Services']],
      [['invoice_prefix', 'Invoice Prefix'], ['invoice_format', 'Invoice Format ({prefix}, {fy}, {seq:04d})']],
    ],
  },
  bank: {
    title: 'Bank / Payout (printed on invoice)', icon: Banknote,
    rows: [
      [['bank_name', 'Bank Name'], ['bank_account', 'Account Number']],
      [['bank_ifsc', 'IFSC Code'], ['bank_upi', 'UPI ID']],
    ],
  },
  invoice: {
    title: 'Invoice Footer & Terms', icon: Settings,
    rows: [
      [['invoice_terms', 'Terms & Conditions (textarea)', 'textarea']],
      [['invoice_footer_note', 'Footer Note (textarea)', 'textarea']],
    ],
  },
};

const AdminBusinessSettings = () => {
  const [s, setS] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/business-settings`, auth())
      .then(r => { setS(r.data.settings || {}); setLoading(false); })
      .catch(() => { toast.error('Failed to load settings'); setLoading(false); });
  }, []);

  const set = (k, v) => setS(prev => ({ ...prev, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/admin/business-settings`, s, auth());
      setS(r.data.settings);
      toast.success('Business settings saved');
    } catch (e) {
      toast.error('Save failed: ' + (e.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  };

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const form = new FormData(); form.append('file', file);
    try {
      const r = await axios.post(`${API}/uploads/image`, form, {
        headers: { ...auth().headers, 'Content-Type': 'multipart/form-data' },
      });
      const url = r.data.url || r.data.file_url || r.data.image_url;
      if (url) { set('logo_url', url); toast.success('Logo uploaded'); }
      else { toast.error('Upload OK but no URL returned'); }
    } catch (err) { toast.error('Upload failed'); }
  };

  if (loading || !s) return <div className="p-8 text-gray-600">Loading...</div>;

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6" data-testid="admin-business-settings">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Business / GST Settings</h1>
          <p className="text-sm text-gray-500">Used on every invoice & shipping label. Fully editable.</p>
        </div>
        <button onClick={save} disabled={saving} data-testid="save-business-settings"
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-700 hover:bg-blue-800 disabled:opacity-60 text-white rounded-lg font-semibold">
          <Save size={18} /> {saving ? 'Saving...' : 'Save All Changes'}
        </button>
      </div>

      {Object.entries(FIELDS).map(([sec, cfg]) => {
        const Icon = cfg.icon;
        return (
          <div key={sec} className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
            <div className="flex items-center gap-2 mb-4 pb-3 border-b">
              <Icon className="text-blue-700" size={20} />
              <h2 className="font-bold text-gray-900">{cfg.title}</h2>
            </div>
            {cfg.rows.map((row, ri) => (
              <div key={ri} className="grid gap-3 mb-3" style={{ gridTemplateColumns: `repeat(${row.length}, minmax(0, 1fr))` }}>
                {row.map(([key, label, kind]) => (
                  <div key={key}>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">{label}</label>
                    {kind === 'textarea' ? (
                      <textarea value={s[key] || ''} onChange={e => set(key, e.target.value)}
                        rows={3} data-testid={`bs-${key}`}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:border-blue-500 focus:outline-none"/>
                    ) : (
                      <input type="text" value={s[key] ?? ''} onChange={e => set(key, e.target.value)}
                        data-testid={`bs-${key}`}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:border-blue-500 focus:outline-none"/>
                    )}
                  </div>
                ))}
              </div>
            ))}
            {sec === 'company' && (
              <div className="mt-2">
                <label className="inline-flex items-center gap-2 px-3 py-2 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg cursor-pointer text-sm font-semibold border border-blue-200">
                  <Upload size={16} /> Upload Logo
                  <input type="file" accept="image/*" onChange={uploadLogo} className="hidden" data-testid="upload-logo"/>
                </label>
                {s.logo_url && <img src={s.logo_url} alt="logo" className="mt-3 h-14 object-contain"/>}
              </div>
            )}
          </div>
        );
      })}

      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
        <p className="text-sm text-amber-900">
          <strong>Tip:</strong> The values you set here appear on every <strong>tax invoice</strong>, <strong>shipping label</strong>, and customer email.
          Invoice numbers auto-increment using the format above.
        </p>
      </div>
    </div>
  );
};

export default AdminBusinessSettings;
